import React, { useEffect, useMemo, useState } from "react";
import {
    IonAccordion,
    IonAccordionGroup,
    IonBadge,
    IonButton,
    IonCol,
    IonGrid,
    IonIcon,
    IonItem,
    IonLabel,
    IonRow,
    IonSearchbar,
    IonText,
    IonToggle,
} from "@ionic/react";
import type { ToggleCustomEvent } from "@ionic/core/components";
import { addOutline, addSharp } from "ionicons/icons";
import type { Reason } from "../AcaSettingsTypes";

interface AcaSettingsComplianceSectionProps {
    data: {
        reasons: Reason[];
    };
    actions: {
        openAdd: () => void;
        toggleReason: (reason: Reason, ev?: ToggleCustomEvent) => void;
    };
}

const AcaSettingsComplianceSection: React.FC<AcaSettingsComplianceSectionProps> = ({data, actions,}) => {
    const { reasons } = data;
    const { openAdd, toggleReason } = actions;

    const [complianceQuery, setComplianceQuery] = useState("");
    const [compExpanded, setCompExpanded] = useState<string[]>([]);

    const activeComplianceCodes = useMemo(
        () =>
            (reasons || [])
                .filter((reason) => reason.status === "ACTIVE")
                .filter((reason) =>
                    (reason.description || "").toLowerCase().includes(complianceQuery.toLowerCase())
                )
                .sort((a, b) => a.description.localeCompare(b.description)),
        [reasons, complianceQuery]
    );

    const inactiveComplianceCodes = useMemo(
        () =>
            (reasons || [])
                .filter((reason) => reason.status === "INACTIVE")
                .filter((reason) =>
                    (reason.description || "").toLowerCase().includes(complianceQuery.toLowerCase())
                )
                .sort((a, b) => a.description.localeCompare(b.description)),
        [reasons, complianceQuery]
    );

    useEffect(() => {
        if (!complianceQuery.trim()) {
            setCompExpanded([]);
            return;
        }
        const open: string[] = [];
        if (activeComplianceCodes.length) {
            open.push("compliance-active");
        }
        if (inactiveComplianceCodes.length) {
            open.push("compliance-inactive");
        }
        setCompExpanded(open);
    }, [complianceQuery]);

    const renderComplianceTiles = (list: Reason[]) => (
        <IonGrid>
            <IonRow>
                {list.map((reason) => (
                    <IonCol size="12" sizeSm="6" sizeMd="4" sizeLg="3" key={reason.id}>
                        <IonItem lines="full">
                            <IonLabel className="ion-text-wrap">
                                <IonText>{reason.description}</IonText>
                            </IonLabel>
                            <IonToggle
                                slot="end"
                                checked={reason.status === "ACTIVE"}
                                onIonChange={(ev) => toggleReason(reason, ev)}
                            />
                        </IonItem>
                    </IonCol>
                ))}
                {list.length === 0 && (
                    <IonCol size="12">
                        <IonItem lines="none">
                            <IonLabel>No items.</IonLabel>
                        </IonItem>
                    </IonCol>
                )}
            </IonRow>
        </IonGrid>
    );

    return (
        <IonAccordion value="sec-compliance">
            <IonItem slot="header" color="light" lines="full">
                <IonLabel>Compliance Flag Codes</IonLabel>
            </IonItem>

            <div slot="content">
                <section className="panel panel-default">
                    <IonGrid>
                        <IonRow className="ion-align-items-end">
                            <IonCol size="12" sizeMd="4">
                                <IonSearchbar
                                    value={complianceQuery}
                                    placeholder="Search compliance codes"
                                    onIonInput={(e) =>
                                        setComplianceQuery((e.detail.value ?? "").toString())
                                    }
                                    onIonClear={() => setComplianceQuery("")}
                                />
                            </IonCol>
                            <IonCol size="12" sizeMd="2" className="btn-col ion-align-self-end">
                                <IonButton onClick={openAdd}>
                                    <IonIcon slot="icon-only" md={addOutline} ios={addSharp} />
                                </IonButton>
                            </IonCol>
                        </IonRow>
                    </IonGrid>

                    <IonAccordionGroup
                        multiple
                        style={{ display: "grid", gap: 6 }}
                        value={compExpanded}
                        onIonChange={(e) =>
                            setCompExpanded(Array.isArray(e.detail.value) ? e.detail.value : [])
                        }
                    >
                        <IonAccordion value="compliance-active">
                            <IonItem slot="header" color="light" lines="full">
                                <IonLabel>Active Compliance Codes</IonLabel>
                                <IonBadge slot="end">{activeComplianceCodes.length}</IonBadge>
                            </IonItem>
                            <div slot="content">{renderComplianceTiles(activeComplianceCodes)}</div>
                        </IonAccordion>

                        <IonAccordion value="compliance-inactive">
                            <IonItem slot="header" color="light" lines="full">
                                <IonLabel>Inactive Compliance Codes</IonLabel>
                                <IonBadge slot="end">{inactiveComplianceCodes.length}</IonBadge>
                            </IonItem>
                            <div slot="content">{renderComplianceTiles(inactiveComplianceCodes)}</div>
                        </IonAccordion>
                    </IonAccordionGroup>
                </section>
            </div>
        </IonAccordion>
    );
};

export default AcaSettingsComplianceSection;